import { useState } from "react";
import {
    Box, TextField, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Typography, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControl, InputLabel, Select, MenuItem, Tooltip
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSelector } from "react-redux";
import { useLazyGetSimAuditLogsQuery } from "../../store/services/logs-service";
import { formatLogDateTime } from "../../utils/formatDateTime";

const SimAuditLog = () => {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [filters, setFilters] = useState({
        email: '',
        dateFrom: '',
        dateTo: '',
        success: '',
    });
    const [pageSize, setPageSize] = useState(50);
    const [allResults, setAllResults] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [trigger, { data, isLoading, error }] = useLazyGetSimAuditLogsQuery();

    const handleSearch = () => {
        setAllResults([]);
        trigger({
            tenancyId,
            ...filters,
            pageSize,
        }).then((res) => {
            if (res.data?.results) {
                setAllResults(res.data.results);
            }
        });
    };

    const handleNextPage = () => {
        if (data?.lastKey) {
            trigger({
                tenancyId,
                ...filters,
                pageSize,
                lastKey: data.lastKey,
            }).then((res) => {
                if (res.data?.results) {
                    setAllResults((prev) => [...prev, ...res.data.results]);
                }
            });
        }
    };

    const columns = [
        { id: 'ToAddress', label: 'To Address', width: 200 },
        { id: 'FromAddress', label: 'From Address', width: 200 },
        { id: 'FromDisplayName', label: 'From Display Name', width: 160 },
        { id: 'Subject', label: 'Subject', width: 200 },
        { id: 'Timestamp', label: 'Timestamp', width: 160 },
        { id: 'Success', label: 'Success', width: 80 },
        { id: 'ErrorMessage', label: 'Error', width: 200 },
        { id: 'SmtpStatus', label: 'SMTP Status', width: 300 },
        { id: 'TenancyId', label: 'Tenancy ID', width: 150 },
        { id: 'UniqueIdentifier', label: 'Unique ID', width: 180 },
        { id: 'MessageId', label: 'Message ID', width: 180 },
    ];

    const formatDate = formatLogDateTime;

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small"
                    label="Email / To Address"
                    value={filters.email}
                    onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Date From"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Date To"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Success</InputLabel>
                    <Select
                        value={filters.success}
                        label="Success"
                        onChange={(e) => setFilters({ ...filters, success: e.target.value })}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Success</MenuItem>
                        <MenuItem value="false">Failed</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleSearch}>
                    Search
                </Button>
            </Stack>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Typography color="error" sx={{ p: 2 }}>
                    Error loading data: {error.status || 'Unknown error'}
                </Typography>
            )}

            {data && (
                <>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 60 }}>View</TableCell>
                                    {columns.map((col) => (
                                        <TableCell key={col.id} sx={{ fontWeight: 600, minWidth: col.width }}>
                                            {col.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allResults.map((row, idx) => (
                                    <TableRow key={row.MessageId || idx} hover>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => setSelectedRow(row)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row.ToAddress || '-'}</TableCell>
                                        <TableCell>{row.FromAddress || '-'}</TableCell>
                                        <TableCell>{row.FromDisplayName || '-'}</TableCell>
                                        <TableCell>{row.Subject || '-'}</TableCell>
                                        <TableCell>{formatDate(row.Timestamp)}</TableCell>
                                        <TableCell>{row.Success !== undefined ? String(row.Success) : '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {row.ErrorMessage || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {row.SmtpStatus || '-'}
                                        </TableCell>
                                        <TableCell>{row.TenancyId || '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.UniqueIdentifier || '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.MessageId || '-'}</TableCell>
                                    </TableRow>
                                ))}
                                {allResults.length === 0 && !isLoading && data && (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} align="center">
                                            No results found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                        {data.lastKey && (
                            <Button variant="outlined" size="small" onClick={handleNextPage}>
                                Load More
                            </Button>
                        )}
                    </Box>
                </>
            )}

            <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} maxWidth="md" fullWidth>
                <DialogTitle>Log Entry Details</DialogTitle>
                <DialogContent dividers>
                    {selectedRow && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {columns.map((col) => (
                                <Box key={col.id}>
                                    <Typography variant="caption" color="text.secondary">{col.label}</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                        {col.id === 'Timestamp'
                                            ? formatDate(selectedRow[col.id])
                                            : col.id === 'Success'
                                                ? (selectedRow[col.id] !== undefined ? String(selectedRow[col.id]) : '-')
                                                : (selectedRow[col.id] || '-')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {!data && !isLoading && !error && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Click Search to view most recent <Tooltip title="Captures email sending events related to simulations, including successes and failures, along with details like recipient, subject, timestamps, and any error messages." arrow placement="bottom"><span style={{ textDecoration: "underline dotted", cursor: "help", color: "inherit" }}>simulation audit logs</span></Tooltip> or enter search criteria to filter results.
                </Typography>
            )}
        </Box>
    );
};

export default SimAuditLog;
